import { memo, useMemo, useCallback } from 'react';
import {
  DatePicker,
  DateInput,
  Calendar,
  FormField,
} from '@cloudscape-design/components';
import { FILTER_OPERATORS } from 'src/contstant/FilterTypes';

interface FilterDatePickerProps {
  value: string | null;
  onChange: (value: string) => void;
  filter?: any;
  placeholder?: string;
  locale?: string;
  label?: string;
}

const FilterDatePicker = memo(
  ({
    value,
    onChange,
    filter,
    placeholder = 'YYYY/MM/DD',
    locale = 'en-GB',
    label,
  }: FilterDatePickerProps) => {
    // Memoize the handleChange function to prevent unnecessary re-renders
    const handleChange = useCallback(
      (event: { detail: { value: string } }) => {
        onChange(event.detail.value);
      },
      [onChange],
    );

    // Memoize the DatePicker component
    const datePickerComponent = useMemo(
      () => (
        <FormField label={label}>
          <DatePicker
            value={value ?? ''}
            onChange={handleChange}
            placeholder={placeholder}
            locale={locale}
          />
        </FormField>
      ),
      [value, handleChange, placeholder, locale, label],
    );

    // Memoize the DateInput and Calendar component
    const dateInputCalendarComponent = useMemo(
      () => (
        <div>
          <FormField label={label}>
            <DateInput
              value={value ?? ''}
              onChange={handleChange}
              placeholder={placeholder}
            />
          </FormField>
          <Calendar value={value ?? ''} onChange={handleChange} locale={locale} />
        </div>
      ),
      [value, handleChange, placeholder, locale, label],
    );

    return filter === undefined ? datePickerComponent : dateInputCalendarComponent;
  },
);

export default FilterDatePicker;
interface FilterOperatorConfig {
  operator: string;
  form: React.FC<FilterFormProps>;
  match: string;
}

interface FilterFormProps {
  value: any;
  onChange: (value: any) => void;
  filter: any;
}

export interface DateFilterConfig {
  key: string;
  propertyLabel: string;
  groupValuesLabel: string;
  operators: FilterOperatorConfig[];
  group?: string;
}
const createDateOperators = (
  operators: string[] = [
    FILTER_OPERATORS.EQUALS,
    FILTER_OPERATORS.NOT_EQUALS,
    FILTER_OPERATORS.GREATER_THAN,
    FILTER_OPERATORS.GREATER_THAN_OR_EQUAL,
    FILTER_OPERATORS.LESS_THAN,
    FILTER_OPERATORS.LESS_THAN_OR_EQUAL,
  ],
): FilterOperatorConfig[] => {
  return operators.map((operator) => ({
    operator,
    form: ({ value, onChange, filter }: FilterFormProps) => (
      <FilterDatePicker
        value={value}
        filter={filter}
        onChange={onChange}
        placeholder="YYYY/MM/DD"
      />
    ),
    match: 'date',
  }));
};

export const createDateFilterConfig = ({
  key,
  propertyLabel,
  groupValuesLabel,
  customOperators,
  group,
}: {
  key: string;
  propertyLabel: string;
  groupValuesLabel: string;
  customOperators?: string[];
  group?: string;
}): DateFilterConfig => ({
  key,
  propertyLabel,
  groupValuesLabel,
  operators: createDateOperators(customOperators),
  group,
});
